import { Configuration, SalesApi } from '@sr-sdks/api-sdk-axios';
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
} from '@tanstack/react-query';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

// ===== API CLIENT =====

/**
 * Create a sales API instance
 */
const getSalesApi = () => {
  return new SalesApi(
    new Configuration(),
    configuration.SERVICES_API_BASE,
    axiosInstance,
  );
};

// ===== QUERY KEYS =====

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const salesKeys = {
  all: ['sales'] as const,
  paginated: (params: SaleQueryParams) =>
    [...salesKeys.all, 'paginated', { ...params }] as const,
};

// ===== API FUNCTIONS =====

export interface SaleQueryParams {
  beneficiaryTitleId?: string;
  page?: number;
  pageSize?: number;
  serviceId?: string;
  sortBy?: string;
}

/**
 * Default parameters for Sales queries. Merged into
 * {@link queryOptionsGetSales} before the query key is built, so an unset
 * page/pageSize/sortBy never creates a distinct cache entry.
 */
export const SALES_DEFAULT_PARAMS = {
  page: 1,
  pageSize: 50,
  sortBy: 'saleDate:desc',
} as const satisfies SaleQueryParams;

/**
 * {@link fetchSales} always has a real `serviceId` by the time it runs —
 * {@link queryOptionsGetSales} only enables the query once one is present —
 * so it's typed as required here rather than falling back to an empty
 * string, which the backend's `@IsUUID()` validation rejects anyway.
 */
type FetchSalesParams = Omit<SaleQueryParams, 'serviceId'> & {
  serviceId: string;
};

/**
 * Fetch sales with optional filters and pagination
 */
export const fetchSales = async (params: FetchSalesParams) => {
  try {
    const { beneficiaryTitleId, page, pageSize, serviceId, sortBy } = params;

    const response = await getSalesApi().saleControllerFindAll(
      serviceId,
      beneficiaryTitleId,
      page,
      pageSize,
      sortBy,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'saleControllerFindAll',
      params: params,
    });
  }
};

// ===== QUERY OPTIONS =====

/**
 * React Query options for a paginated sales list.
 * Skips the fetch when `serviceId` is missing (caller opt-out).
 */
export const queryOptionsGetSales = (params?: SaleQueryParams) => {
  const merged = { ...SALES_DEFAULT_PARAMS, ...params };
  const { serviceId } = merged;

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- lint-backlog: queryKey omits a value the queryFn reads; adding it changes caching
  return queryOptions({
    queryFn: serviceId ? () => fetchSales({ ...merged, serviceId }) : skipToken,
    queryKey: salesKeys.paginated(merged),
  });
};

// ===== QUERY HOOKS =====

/** Narrow opt-in flags for {@link useSales} call sites. */
export type UseSalesOptions = {
  enabled?: boolean;
  /** Opt in for pagination UX; not set on shared query options. */
  placeholderData?: typeof keepPreviousData;
};

/**
 * Hook for fetching sales with optional filters.
 * `options` is intentionally narrow so call sites opt into behaviour like
 * `placeholderData: keepPreviousData` without affecting every consumer.
 */
export const useSales = (
  params?: SaleQueryParams,
  options?: UseSalesOptions,
) => {
  return useQuery({
    ...queryOptionsGetSales(params),
    ...options,
  });
};
